// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';

// Material
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

// Local library
import { WagerFacade } from '@amwager/wager/data-access';
import {
  dutchAmounts,
  dutchWagersService,
  ticketRequest,
} from '@amwager/util-ts';
import {
  MatNotificationService,
  UnsubscribeObservableService,
TranslatePipe
} from '@amwager/util';

// Third party
import _ from 'lodash';
import { takeUntil } from 'rxjs';

/**
 * Dutch wager preview dialog — per-runner amounts, payouts, submit/save
 */
@Component({
  selector: 'feature-mat-dutch',
  templateUrl: './dutch.component.html',
  styleUrls: ['./dutch.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DutchComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy
{
  // Display fields
  eventName!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dutchTicket: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  poolMatrix: any;
  totalCost: number = 0;
  baseAmount: number = 0;
  balanceAmount: number = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pool: any;
  modifier: string = '';
  selectedRunners: string = '';

  // Re-build inputs
  dutchEnum: number = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  runnersEnmList: any;
  defaultModifier: string = 'Straight';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedRace: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedEvent: any;
  maxWagerTotal: number = 0;
  isBlockJurisdiction: boolean = false;
  isBlockJurisdictionMsg: string = '';

  // Computed projection state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dutchHolder: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dutchAmountHolder: any;
  minPayout: number = 0;
  maxPayout: number = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toRemoveTickets: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<DutchComponent>,
    private readonly wagerFacade: WagerFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly matNotificationService: MatNotificationService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: any;
      actionShowYesBtn?: boolean;
      actionShowNoBtn?: boolean;
    }
  ) {
    super();

    // Re-build rows whenever the race matrix updates after dialog open.
    this.wagerFacade
      .getRaceMatrix$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback === undefined) return;

        const newMatrix =
          callback[this.pool?.PoolId]?.response?.ToteResponse;

        if (!newMatrix) return;

        this.poolMatrix = newMatrix;

        if (
          this.eventName &&
          this.dutchTicket &&
          this.poolMatrix &&
          this.baseAmount &&
          this.pool &&
          this.modifier &&
          this.selectedRunners
        ) {
          this.initBetRows();
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Pull dialog inputs and run the first projection pass
   * @returns {void}
   */
  ngOnInit(): void {
    const p = this.data?.payload ?? {};
    this.eventName = p.eventName;
    this.dutchTicket = p.dutchTicket;
    this.poolMatrix = p.poolMatrix;
    this.baseAmount = p.baseAmount;
    this.totalCost = p.totalCost;
    this.balanceAmount = p.balanceAmount;
    this.pool = p.pool;
    this.modifier = p.modifier;
    this.selectedRunners = p.selectedRunners;

    this.dutchEnum = p.dutchEnum;
    this.runnersEnmList = p.runnersEnmList;
    this.defaultModifier = p.defaultModifier ?? 'Straight';
    this.events = p.events;
    this.selectedRace = p.selectedRace;
    this.selectedEvent = p.selectedEvent;
    this.maxWagerTotal = p.maxWagerTotal;
    this.isBlockJurisdiction = p.isBlockJurisdiction;
    this.isBlockJurisdictionMsg = p.isBlockJurisdictionMsg;

    this.toRemoveTickets = [];
    this.initBetRows();
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();
  }

  /**
   * Re-compute dutch projections (per-row amounts, ROI, total cost)
   * @returns {void}
   */
  initBetRows(): void {
    if (!this.poolMatrix || !this.pool) return;

    this.dutchTicket = new dutchWagersService().buildDutchWagers(
      this.dutchEnum,
      this.runnersEnmList,
      this.defaultModifier,
      this.pool,
      this.baseAmount,
      this.poolMatrix,
      this.events,
      this.selectedRace,
      this.selectedEvent,
      this.maxWagerTotal,
      this.isBlockJurisdiction,
      this.isBlockJurisdictionMsg,
      this.toRemoveTickets
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runners: any[] = [];
    for (let i = 0; i < this.dutchTicket?.Wagers.length; i++) {
      runners.push(this.dutchTicket?.Wagers[i].getRunnerList());
    }
    this.dutchHolder = new dutchAmounts(runners, this.pool, this.baseAmount);

    this.dutchHolder.initBaseParams(this.poolMatrix);

    if (this.dutchTicket?.DutchType == 1) {
      this.dutchAmountHolder = this.dutchHolder.calcAmountByBaseAmount(
        this.baseAmount,
        this.poolMatrix
      );
    } else {
      this.dutchAmountHolder = this.dutchHolder.calcAmountByBasePayout(
        this.baseAmount,
        this.poolMatrix
      );
    }

    this.dutchTicket.DutchProjections['ProjectedMaxROI'] = this.dutchHolder.calcROI(
      this.dutchHolder.MaxPayout
    );
    this.dutchTicket.DutchProjections['ProjectedMinROI'] = this.dutchHolder.calcROI(
      this.dutchHolder.MinPayout
    );

    this.minPayout = this.dutchHolder.calcROI(this.dutchHolder.MinPayout);
    this.maxPayout = this.dutchHolder.calcROI(this.dutchHolder.MaxPayout);
    this.totalCost = this.dutchTicket.calcAmount();

    this.cdRef.markForCheck();
  }

  /**
   * Remove a row, redistribute the projection and refresh the table
   * @param {number} id Index of the row to remove
   * @returns {void}
   */
  removeBet(id: number): void {
    if (id === undefined || id === null) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let amountHolder!: any;
    this.dutchTicket.Wagers.splice(id, 1);

    this.dutchTicket = new ticketRequest(
      this.dutchTicket.RunId,
      this.dutchTicket.EventId,
      this.dutchTicket.RaceId,
      this.dutchTicket.Wagers,
      this.dutchTicket.DutchType,
      this.dutchTicket.DutchProjections
    );

    const comboToRemove = [];
    for (
      let ctr = 0;
      ctr < this.dutchHolder.RunnersByWager[id].length;
      ctr++
    ) {
      comboToRemove.push(this.dutchHolder.RunnersByWager[id][ctr][0]);
    }
    this.toRemoveTickets.push(comboToRemove);

    this.dutchHolder.RunnersByWager.splice(id, 1);
    this.dutchHolder.initBaseParams(this.poolMatrix);

    if (this.dutchTicket?.DutchType == 1) {
      amountHolder = this.dutchHolder.calcAmountByBaseAmount(
        this.baseAmount,
        this.poolMatrix
      );
    } else {
      amountHolder = this.dutchHolder.calcAmountByBasePayout(
        this.baseAmount,
        this.poolMatrix
      );
    }

    for (let i = 0; i < this.dutchTicket.Wagers.length; i++) {
      this.dutchTicket.Wagers[i].BaseAmount = amountHolder[i][0];
    }

    this.initBetRows();
    this.cdRef.markForCheck();
  }

  /**
   * Submit the entire dutch ticket (dispatches bet-now)
   * @returns {void}
   */
  submitAll(): void {
    if (this.dutchTicket?.calcAmount() > this.balanceAmount) {
      this.matNotificationService.showAlert(
        'Info',
        'Insufficient Funds.',
        '',
        '',
        'Deposit Now'
      );
      return;
    }

    this.dialogRef.close({
      data: {
        betType: 'bet-now',
        typeIs: 'dutch',
        dutchTicket: this.dutchTicket,
      },
    });
  }

  /**
   * Save the entire dutch ticket (dispatches save-bet)
   * @returns {void}
   */
  saveAll(): void {
    this.dialogRef.close({
      data: {
        betType: 'save-bet',
        typeIs: 'dutch',
        dutchTicket: this.dutchTicket,
      },
    });
  }

  /**
   * Cancel the dialog (no data payload)
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close({ data: null });
  }
}
